import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

// One Letter layout for the PDF shown, downloaded and sent.
export const DOCUMENT_PAGE = { width: 215.9, height: 279.4, top: 8, bottom: 8, left: 10, right: 10 };
export const DOCUMENT_WIDTH_PX = (DOCUMENT_PAGE.width - DOCUMENT_PAGE.left - DOCUMENT_PAGE.right) * 96 / 25.4;
export const DOCUMENT_PRINT_STYLE = `
  @page { size: letter portrait; margin: 8mm 10mm; }
  @media print {
    html, body { margin: 0 !important; padding: 0 !important; background: white !important; }
    .no-print { display: none !important; }
  }
`;

// These rules also apply to canvas exports: html2canvas does not apply @media print.
export const DOCUMENT_PDF_STYLE = `
  .document-pdf-source { position: fixed; left: -100000px; top: 0; width: ${DOCUMENT_WIDTH_PX}px; pointer-events: none; }
  .document-pdf {
    box-sizing: border-box;
    width: ${DOCUMENT_WIDTH_PX}px !important;
    min-width: ${DOCUMENT_WIDTH_PX}px !important;
    max-width: ${DOCUMENT_WIDTH_PX}px !important;
    flex-shrink: 0;
    padding: 0 !important;
    background: white;
    font-size: 12px !important;
    line-height: 1.4;
    break-inside: auto !important;
    page-break-inside: auto !important;
  }
  .document-pdf * { box-sizing: border-box; }
  .document-pdf p, .document-pdf span, .document-pdf td,
  .document-pdf .invoice-card-body, .document-pdf .info-row {
    font-size: 12px !important;
    overflow-wrap: anywhere;
  }
  .document-pdf p { margin-bottom: 3px !important; }
  .document-pdf img { max-width: 200px !important; max-height: 50px !important; object-fit: contain; }
  .document-pdf .pdf-title { font-size: 24px !important; }
  .document-pdf .row-2-col, .document-pdf .row-3-col { gap: 14px !important; }
  .document-pdf .row-2-col > div, .document-pdf .row-3-col > div { min-width: 0; }
  /* Summary section: side-by-side notes and totals */
  .document-pdf .pdf-summary {
    display: flex !important;
    flex-direction: row !important;
    justify-content: space-between !important;
    align-items: flex-start !important;
    width: 100% !important;
    gap: 16px !important;
  }
  .document-pdf .pdf-summary > div:first-child {
    flex: 1 1 auto !important;
    min-width: 0 !important;
    width: auto !important;
  }
  .document-pdf .pdf-summary .totals-card {
    flex: 0 0 320px !important;
    width: 320px !important;
    min-width: 320px !important;
    max-width: 320px !important;
    margin-left: auto !important;
    margin-top: 0 !important;
  }
  .document-pdf .invoice-card-header { font-size: 11.5px !important; padding: 6px 12px !important; }
  .document-pdf .invoice-card-body { padding: 10px 14px !important; }
  .document-pdf .info-row { padding: 4px 0 !important; }
  .document-pdf .table-responsive { overflow: visible !important; }
  .document-pdf table { width: 100% !important; break-inside: auto !important; page-break-inside: auto !important; }
  .document-pdf th { font-size: 11px !important; }
  .document-pdf th, .document-pdf td { padding: 6px 8px !important; }
  .document-pdf thead { display: table-header-group; }
  .document-pdf tr, .document-pdf [data-pdf-keep], .document-pdf .totals-card {
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .document-pdf .totals-card { width: 320px !important; min-width: 320px !important; max-width: 320px !important; flex: 0 0 320px !important; }
  .document-pdf .totals-row { padding: 5px 10px !important; }
  @media print {
    .document-pdf { width: 100% !important; min-width: 0 !important; max-width: 100% !important; margin: 0 !important; border: none !important; box-shadow: none !important; }
    /* main.css keeps every div together; tables and long notes must be able to flow. */
    .document-pdf div { break-inside: auto; page-break-inside: auto; }
    .document-pdf .invoice-card, .document-pdf .border.rounded { overflow: visible !important; }
    .document-pdf * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
`;

/** Page cuts are in canvas pixels. Never split a row that fits on a page. */
export function getPdfPageSlices(height, pageHeight, keepRegions = [], tables = []) {
  if (!Number.isFinite(height) || height <= 0 || !Number.isFinite(pageHeight) || pageHeight < 1) {
    throw new Error("Invalid PDF page dimensions");
  }
  const pages = [];
  let start = 0;
  while (start < height) {
    const pageStart = start;
    const table = tables.find(({ header, bottom }) => pageStart >= header.end && pageStart < bottom);
    const header = table && table.header.end - table.header.start < pageHeight / 2 ? table.header : null;
    const capacity = Math.floor(pageHeight - (header ? header.end - header.start : 0));
    let end = Math.min(height, start + capacity);
    for (const region of keepRegions) {
      if (region.start > start && region.start < end && region.end > end && region.end - region.start <= capacity) {
        end = region.start;
      }
    }
    // An oversized row/notes block must still advance rather than create blank pages.
    end = Math.max(start + 1, Math.floor(end));
    pages.push({ start, end, header });
    start = end;
  }
  return pages;
}

/**
 * Extracts visible text elements with their layout positions and font sizes
 * so they can be embedded as a selectable, searchable text layer in the PDF.
 */
export function extractDocumentTextItems(element, bounds) {
  if (!element || !bounds || typeof document === "undefined") return [];
  const textItems = [];
  const textNodes = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
  let textNode;
  while ((textNode = textNodes.nextNode())) {
    const rawText = textNode.textContent;
    if (!rawText || !rawText.trim() || textNode.parentElement?.closest("style, script")) {
      continue;
    }
    const parent = textNode.parentElement;
    const computed = typeof window !== "undefined" && window.getComputedStyle ? window.getComputedStyle(parent) : null;
    const fontSize = computed ? parseFloat(computed.fontSize) || 12 : 12;
    const fontWeight = computed ? computed.fontWeight : "normal";
    const isBold = fontWeight === "bold" || parseInt(fontWeight, 10) >= 600;

    const range = document.createRange();
    range.selectNodeContents(textNode);
    const rects = (typeof range.getClientRects === "function" ? Array.from(range.getClientRects()) : [])
      .filter(r => r && r.width > 0 && r.height > 0);
    if (!rects.length) continue;

    if (rects.length === 1) {
      const rect = rects[0];
      const trimmed = rawText.replace(/\s+/g, " ").trim();
      if (trimmed) {
        textItems.push({
          text: trimmed,
          left: rect.left - bounds.left,
          top: rect.top - bounds.top,
          width: rect.width,
          height: rect.height,
          fontSize,
          isBold,
        });
      }
    } else {
      const regex = /\S+/g;
      let match;
      const wordItems = [];
      while ((match = regex.exec(rawText)) !== null) {
        const wordRange = document.createRange();
        wordRange.setStart(textNode, match.index);
        wordRange.setEnd(textNode, match.index + match[0].length);
        const wordRect = typeof wordRange.getBoundingClientRect === "function" ? wordRange.getBoundingClientRect() : null;
        if (wordRect && wordRect.width > 0 && wordRect.height > 0) {
          wordItems.push({
            word: match[0],
            left: wordRect.left - bounds.left,
            top: wordRect.top - bounds.top,
            width: wordRect.width,
            height: wordRect.height,
            bottom: wordRect.bottom - bounds.top,
          });
        }
      }

      let currentLine = null;
      for (const item of wordItems) {
        if (!currentLine) {
          currentLine = {
            words: [item.word],
            left: item.left,
            top: item.top,
            right: item.left + item.width,
            bottom: item.bottom,
            height: item.height,
          };
        } else {
          const lineMidY = (currentLine.top + currentLine.bottom) / 2;
          const wordMidY = (item.top + item.bottom) / 2;
          if (Math.abs(lineMidY - wordMidY) <= item.height * 0.6) {
            currentLine.words.push(item.word);
            currentLine.right = Math.max(currentLine.right, item.left + item.width);
            currentLine.bottom = Math.max(currentLine.bottom, item.bottom);
            currentLine.top = Math.min(currentLine.top, item.top);
          } else {
            textItems.push({
              text: currentLine.words.join(" "),
              left: currentLine.left,
              top: currentLine.top,
              width: currentLine.right - currentLine.left,
              height: currentLine.bottom - currentLine.top,
              fontSize,
              isBold,
            });
            currentLine = {
              words: [item.word],
              left: item.left,
              top: item.top,
              right: item.left + item.width,
              bottom: item.bottom,
              height: item.height,
            };
          }
        }
      }
      if (currentLine) {
        textItems.push({
          text: currentLine.words.join(" "),
          left: currentLine.left,
          top: currentLine.top,
          width: currentLine.right - currentLine.left,
          height: currentLine.bottom - currentLine.top,
          fontSize,
          isBold,
        });
      }
    }
  }
  return textItems;
}

export async function createDocumentPdf(element) {
  if (!element) throw new Error("The document is not ready to export");
  if (document.fonts?.ready) await document.fonts.ready;
  await Promise.all(Array.from(element.querySelectorAll("img"), image => {
    if (image.complete) {
      if (!image.naturalWidth) throw new Error("A document image could not be loaded");
      return undefined;
    }
    return new Promise((resolve, reject) => {
      image.addEventListener("load", resolve, { once: true });
      image.addEventListener("error", () => reject(new Error("A document image could not be loaded")), { once: true });
    });
  }));

  const bounds = element.getBoundingClientRect();
  if (!bounds.width || !bounds.height) throw new Error("The document has no printable content");
  const relativeBounds = node => {
    const rect = node.getBoundingClientRect();
    return { start: rect.top - bounds.top, end: rect.bottom - bounds.top };
  };
  const keepRegions = Array.from(element.querySelectorAll("tr, [data-pdf-keep], .totals-card"), relativeBounds);
  // Rows and notes taller than a page still need cuts between text lines.
  const textNodes = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
  let textNode;
  while ((textNode = textNodes.nextNode())) {
    if (!textNode.textContent.trim() || textNode.parentElement?.closest("style, script")) continue;
    const range = document.createRange();
    range.selectNodeContents(textNode);
    const textRects = typeof range.getClientRects === "function" ? Array.from(range.getClientRects()) : [];
    for (const rect of textRects) {
      if (rect.height) keepRegions.push({ start: rect.top - bounds.top, end: rect.bottom - bounds.top });
    }
  }
  const tables = Array.from(element.querySelectorAll("table"))
    .filter(table => table.querySelector("thead"))
    .map(table => ({ header: relativeBounds(table.querySelector("thead")), bottom: relativeBounds(table).end }));

  const textItems = extractDocumentTextItems(element, bounds);

  const canvas = await html2canvas(element, {
    backgroundColor: "#ffffff", useCORS: true, logging: false,
    // Bound memory and canvas height for long quotes, independently of display DPI.
    scale: Math.min(2, 30000 / bounds.height, Math.sqrt(16000000 / (bounds.width * bounds.height))),
    windowWidth: Math.max(1200, document.documentElement.clientWidth),
    scrollX: 0, scrollY: 0,
  });
  if (!canvas.width || !canvas.height) throw new Error("The PDF capture is empty");

  const pdf = new jsPDF({ unit: "mm", format: "letter", orientation: "portrait", compress: true });
  const width = pdf.internal.pageSize.getWidth() - DOCUMENT_PAGE.left - DOCUMENT_PAGE.right;
  const height = pdf.internal.pageSize.getHeight() - DOCUMENT_PAGE.top - DOCUMENT_PAGE.bottom;
  // Use the actual captured width; pixels must never be interpreted as millimetres
  // or divided by a guessed devicePixelRatio/resolution.
  const pixelsPerMm = canvas.width / width;
  const scaleY = canvas.height / bounds.height;
  const scaleRegion = region => ({ start: Math.floor(region.start * scaleY), end: Math.ceil(region.end * scaleY) });
  const pages = getPdfPageSlices(canvas.height, height * pixelsPerMm, keepRegions.map(scaleRegion),
    tables.map(table => ({ header: scaleRegion(table.header), bottom: Math.ceil(table.bottom * scaleY) })));

  pages.forEach(({ start, end, header }, index) => {
    if (index) pdf.addPage();
    const headerHeight = header ? header.end - header.start : 0;
    const pageCanvas = document.createElement("canvas");
    pageCanvas.width = canvas.width;
    pageCanvas.height = end - start + headerHeight;
    const context = pageCanvas.getContext("2d");
    if (!context) throw new Error("PDF canvas rendering is unavailable");
    if (header) context.drawImage(canvas, 0, header.start, canvas.width, headerHeight, 0, 0, canvas.width, headerHeight);
    context.drawImage(canvas, 0, start, canvas.width, end - start, 0, headerHeight, canvas.width, end - start);
    pdf.addImage(pageCanvas, "PNG", DOCUMENT_PAGE.left, DOCUMENT_PAGE.top, width, pageCanvas.height / pixelsPerMm, undefined, "FAST");
    pageCanvas.width = pageCanvas.height = 0;

    // Overlay selectable and searchable text layer on top of the rendered page.
    // PDF Rendering Mode 3 (3 Tr) makes the text invisible so typography and layout
    // remain 100% pixel-perfect while allowing text selection, copying and search.
    if (typeof pdf.text === "function" && textItems.length > 0) {
      if (pdf.internal?.write) pdf.internal.write("3 Tr\n");

      const renderItem = (item, canvasY) => {
        const x_mm = DOCUMENT_PAGE.left + item.left * (width / bounds.width);
        const y_mm = DOCUMENT_PAGE.top + canvasY / pixelsPerMm;
        const fontSizePt = Math.max(6, Math.min(36, item.fontSize * 0.75));

        if (pdf.setFont) {
          pdf.setFont("Helvetica", item.isBold ? "bold" : "normal");
        }
        if (pdf.setFontSize) {
          pdf.setFontSize(fontSizePt);
        }
        const cleanText = item.text.replace(/\u00a0/g, " ");
        try {
          pdf.text(cleanText, x_mm, y_mm, { baseline: "top" });
        } catch (e) {
          // If any character fails WinAnsiEncoding, retry with ASCII fallback
          const asciiText = cleanText.replace(/[^\x20-\x7E]/g, " ");
          pdf.text(asciiText, x_mm, y_mm, { baseline: "top" });
        }
      };

      if (header) {
        for (const item of textItems) {
          const itemCanvasTop = item.top * scaleY;
          const itemCanvasBottom = (item.top + item.height) * scaleY;
          if (itemCanvasTop >= header.start && itemCanvasBottom <= header.end) {
            renderItem(item, itemCanvasTop - header.start);
          }
        }
      }

      for (const item of textItems) {
        const itemCanvasTop = item.top * scaleY;
        if (itemCanvasTop >= start && itemCanvasTop < end) {
          renderItem(item, headerHeight + (itemCanvasTop - start));
        }
      }

      if (pdf.internal?.write) pdf.internal.write("0 Tr\n");
    }
  });
  return pdf;
}
